import { round } from 'lodash'

/**
 * The difficulty presets the lobby offers, hardest last.
 *
 * `difficulty` is the single knob every other difficulty option is derived
 * from — see `difficultyPresetOptions`. Numbers are deliberately not evenly
 * spaced; they are the values the game has actually been balanced against.
 */
export const DIFFICULTY_PRESETS: { key: string, label: string, difficulty: number }[] = [
  { key: 'Cakewalk', label: 'Cakewalk 🍰', difficulty: 2 },
  { key: 'Very Easy', label: 'Very Easy 😀', difficulty: 3 },
  { key: 'Easy', label: 'Easy 😀', difficulty: 4 },
  { key: 'Normal', label: 'Normal 🙂', difficulty: 5 },
  { key: 'Challenging', label: 'Challenging 😐', difficulty: 5.5 },
  { key: 'Tough', label: 'Tough 😬', difficulty: 6 },
  { key: 'Brutal', label: 'Brutal 😢', difficulty: 6.5 },
  { key: 'Painful', label: 'Painful 😮', difficulty: 7 },
  { key: 'Crippling', label: 'Crippling 😭', difficulty: 7.5 },
  { key: 'Masochistic', label: 'Masochistic 👿', difficulty: 8 },
  { key: 'Obscene', label: 'Obscene 😨', difficulty: 8.5 },
  { key: 'Lethal', label: 'Lethal ☠️', difficulty: 9 },
  { key: 'Futile', label: 'Futile 😖', difficulty: 9.5 },
]

/** Every option key a difficulty preset writes. */
export interface DifficultyPresetOptions {
  difficulty_settings: number
  starting_cash: number
  starting_population: number
  planet_rolls_count: number
  difficulty_recession_length_multiplier: number
  difficulty_recession_revenue_multiplier: number
  difficulty_planet_resource_requirements: number
  planet_upgrade_speed: number
  difficulty_geomagnetic_storms: number
  difficulty_credit_rating_impact: number
  difficulty_bond_spread: number
  config_credit_rating_interest_spread: number
  boss_start_year: number
  boss_spawn_probability: number
}

/**
 * Derive the full option set for a difficulty number.
 *
 * One source of truth: the lobby's difficulty dropdown and the headless
 * simulator both call this, so a balance run can never be measuring a
 * difficulty curve the real game doesn't use.
 */
export function difficultyPresetOptions(difficulty: number): DifficultyPresetOptions {
  return {
    difficulty_settings: difficulty, // 5 is normal difficulty (challenging), 8 is hardest
    starting_cash: Math.round(500 / difficulty),
    starting_population: Math.round(65 / difficulty),
    planet_rolls_count: Math.round(40 / (difficulty + 5)),
    difficulty_recession_length_multiplier: round((difficulty + 30) / 35, 3), // 15/10 35/30
    difficulty_recession_revenue_multiplier: round(45 / (difficulty + 40), 3), // 15/10 45/40
    difficulty_planet_resource_requirements: round((difficulty + 5) / 10, 2),
    planet_upgrade_speed: round(10 / (difficulty + 5), 2),
    difficulty_geomagnetic_storms: round(Math.min(Math.max((difficulty - 3) / 2.5, 0), 1.2), 2), // Max rate 20% increase, min is 0%
    difficulty_credit_rating_impact: round(20 / (difficulty + 15), 2), // 10/5 20/15
    difficulty_bond_spread: round((1 - (difficulty / 5)) / 50, 3),
    config_credit_rating_interest_spread: round(8 / (difficulty + 3), 2),
    // Boss invaders (only bite when boss_invaders_enabled is on — the preset
    // never touches that toggle): harder games meet the boss earlier and see
    // it respawn sooner after a kill. Its levy/volley already track the
    // economy, so arrival year and uptime are the difficulty axes.
    boss_start_year: Math.round(280 / (difficulty + 3)), // 35/5 (Normal keeps the stock year)
    boss_spawn_probability: round(difficulty / 20, 3), // 0.25/5 (stock odds at Normal)
  }
}

/**
 * Look a preset up by its key ('Masochistic'), its label, or its number as a
 * string. Returns undefined when nothing matches.
 */
export function findDifficultyPreset(name: string | number) {
  if (typeof name === 'number') {
    return DIFFICULTY_PRESETS.find(p => p.difficulty === name)
  }
  const needle = name.trim().toLowerCase()
  return DIFFICULTY_PRESETS.find(p =>
    p.key.toLowerCase() === needle ||
    p.label.toLowerCase() === needle ||
    String(p.difficulty) === needle)
}

/**
 * The Nth hardest preset, 1-based: `nthHardestDifficulty(1)` is the hardest
 * setting in the list.
 */
export function nthHardestDifficulty(n: number) {
  const index = DIFFICULTY_PRESETS.length - n
  return DIFFICULTY_PRESETS[Math.min(Math.max(index, 0), DIFFICULTY_PRESETS.length - 1)]
}
